<template>
    <div v-if="addNew && !isEditing" class="address-card-new" @click="toggleEdit()">
        <PlusIcon/>
        <p>New Address</p>
    </div>
    <div v-else class="address-card">
        <h5 v-if="customerAddress.is_default == 1" class="default-label">DEFAULT</h5>
        <div v-if="isEditing">
            <AddressInput
                :regions="props.regions"
                :enableValidation="true"
                v-model:first_name="customerAddress.shipping_first_name"
                v-model:last_name="customerAddress.shipping_last_name"
                v-model:street1="customerAddress.shipping_street1"
                v-model:street2="customerAddress.shipping_street2"
                v-model:street3="customerAddress.shipping_street3"
                v-model:city="customerAddress.shipping_city"
                v-model:state="customerAddress.shipping_state"
                v-model:zip="customerAddress.shipping_postal_code"
                v-model:country="customerAddress.shipping_country_code"
                v-model:phone="customerAddress.shipping_phone"
                :errors="errorsMap"
            />
        </div>
        <div v-else>
            <p>{{ customerAddress.shipping_first_name }} {{ customerAddress.shipping_last_name }}</p>
            <p>{{ customerAddress.shipping_street1 }}</p>
            <p v-if="customerAddress.shipping_street2">{{ customerAddress.shipping_street2 }}</p>
            <p v-if="customerAddress.shipping_street3">{{ customerAddress.shipping_street3 }}</p>
            <p>{{ customerAddress.shipping_city }} {{ customerAddress.shipping_state }}, {{ customerAddress.shipping_postal_code }}</p>
            <p>{{ customerAddress.shipping_country_code }}</p>
            <p>Phone: {{ customerAddress.shipping_phone }}</p>
        </div>
        <div class="actions">
            <RothcoCta id="edit_button" @click.prevent="toggleEdit(); restoreOriginalAddress();" :inertia="false" cta-type="button" class="primary small" :button_icon="null">{{ isEditing ? 'Cancel Edit' : 'Edit' }}</RothcoCta>
            <RothcoCta id="save_button" v-if="isEditing" @click.prevent="saveAddress()" :inertia="false" cta-type="button" class="primary small" :button_icon="null">Save</RothcoCta>
            <button id="delete_button" v-if="!addNew" class="secondary small" @click="removeAddress()"><DeleteIcon class="secondary" style="margin:0;"/></button>
        </div>
        <div class="actions">
            <RothcoCta id="set_as_default" v-if="!addNew && !customerAddress.is_default" @click="setDefault()" :inertia="false" cta-type="link" class="secondary small" :button_icon="null">
                Set as Default
            </RothcoCta>
        </div>
    </div>
</template>
<script lang="ts" setup>
    import { ref, computed, watch, type PropType } from "vue";
    import RothcoCta from "./RothcoCta.vue";
    import AddressInput from "./D2CAddressInput.vue";
    import { useForm } from "@/helpers/composables/useForm";
    import { string, number, date, boolean, object } from "yup";
    import { useAddressBook } from "@/helpers/composables/addressBook";
    import { useSettingsStore } from "@/store/settings";
    import { storeToRefs } from "pinia";
    import PlusIcon from "@icons/plus.svg";
    import DeleteIcon from "@icons/delete-black.svg";
    interface Country {
        alpha2code: string;
        alpha3code: string;
        name: string;
    }

    interface Region {
        code: string;
        name: string;
    }

    const props = defineProps({
        addNew: {
            type: Boolean,
            default: false
        },
        regions: {
            type: Array,
            default(){
                return []
            }
        }
    })

    const emit = defineEmits(["addNewAddress","removeAddress","setNewDefault"]);

    const customerAddress = defineModel({default: {
        shipping_first_name: null,
        shipping_last_name: null,
        shipping_street1: null,
        shipping_street2: null,
        shipping_street3: null,
        shipping_city: null,
        shipping_state: null,
        shipping_postal_code: null,
        shipping_country_code: "USA",
        shipping_phone: null,
        is_default: 0
    }})
    const originalCustomerAddress = ref({});

    watch(()=>customerAddress.value, (newVal)=>{
        Object.keys(newVal).forEach((key)=>{
            form[key] = newVal[key];
        })
    },{deep: true});

    let form = useForm({
        useInertia: false,
        inertiaAlias: "CustomerAddress",
        schema: object({
            shipping_first_name: string()
                .required("First Name / Company is required")
                .default(customerAddress?.value?.shipping_first_name),
            shipping_last_name: string().notRequired()
                .default(customerAddress?.value?.shipping_last_name),
            shipping_street1: string()
                .required("Address is required")
                .default(customerAddress?.value?.shipping_street1),
            shipping_street2: string().notRequired()
                .default(customerAddress?.value?.shipping_street2),
            shipping_street3: string().notRequired()
                .default(customerAddress?.value?.shipping_street3),
            shipping_city: string()
                .required("City is required")
                .default(customerAddress?.value?.shipping_city),
            shipping_state: string()
                .required("State is required")
                .default(customerAddress?.value?.shipping_state),
            shipping_postal_code: string()
                .required("Postal Code is required")
                .default(customerAddress?.value?.shipping_postal_code),
            shipping_country_code: string()
                .required("Country is required")
                .default(customerAddress?.value?.shipping_country_code),
            shipping_phone: string().notRequired()
                .default(customerAddress?.value?.shipping_phone),
        })
    });

    const errorsMap = computed(()=>{
        return Object.keys(form.errors).reduce((acc,key)=>{
            switch(key){
                case 'shipping_postal_code':
                    acc['zip'] = form.errors[key];
                    return acc;
                case 'shipping_country_code':
                    acc['country'] = form.errors[key];
                    return acc;
                default:
                    acc[key.replace('shipping_','')] = form.errors[key];
                    return acc;
            }
        }, {});
    })

    const { user_id } = storeToRefs(useSettingsStore());

    const {
        createAddress,
        updateAddress,
        setAddressToDefault,
    } = useAddressBook();

    const isEditing = ref(false);

    const toggleEdit = ()=>{
        isEditing.value = !isEditing.value;
        if(isEditing.value){
            originalCustomerAddress.value = JSON.parse(JSON.stringify(customerAddress.value));
            form.validate({all: true});
        }
    }

    const restoreOriginalAddress = ()=>{
        customerAddress.value = JSON.parse(JSON.stringify(originalCustomerAddress.value));
    }

    const removeAddress = ()=>{
        emit('removeAddress', customerAddress.value.customer_address_id);
    }

    let isFullAddress = computed(()=>{
        return form.shipping_first_name != null && form.shipping_first_name != '' &&
        form.shipping_street1 != null && form.shipping_street1 != '' &&
        form.shipping_city != null && form.shipping_city != '' &&
        form.shipping_state != null && form.shipping_state != '' &&
        form.shipping_postal_code != null && form.shipping_postal_code &&
        form.shipping_country_code != null && form.shipping_country_code != '';
    });
    
    const saveAddress = ()=>{
        if(!isFullAddress.value){
            form.validate({all: true});
            return;
        }
        if(Object.keys(errorsMap.value).length > 0) return;
        const payload = {
            customer_address_id: customerAddress.value.customer_address_id,
            shipping_first_name: form['shipping_first_name'],
            shipping_last_name: form['shipping_last_name'],
            shipping_street1: form['shipping_street1'],
            shipping_street2: form['shipping_street2'],
            shipping_street3: form['shipping_street3'],
            shipping_city: form['shipping_city'],
            shipping_state: form['shipping_state'],
            shipping_postal_code: form['shipping_postal_code'],
            shipping_country_code: form['shipping_country_code'],
            shipping_phone: form['shipping_phone'],
        }

        if(payload.customer_address_id){
            updateAddress(payload, {
                afterFetch: (ctx)=>{
                    toggleEdit();
                    customerAddress.value = {
                        ...ctx.data
                    }
                }
            })
        }else{
            createAddress({...payload, user_id: user_id.value}, {
                afterFetch: (ctx)=>{
                    toggleEdit();
                    emit("addNewAddress", ctx.data);
                    customerAddress.value = {
                        shipping_first_name: null,
                        shipping_last_name: null,
                        shipping_street1: null,
                        shipping_street2: null,
                        shipping_street3: null,
                        shipping_city: null,
                        shipping_state: null,
                        shipping_postal_code: null,
                        shipping_country_code: "USA",
                        shipping_phone: null,
                        is_default: 0
                    }
                }
            })
        }
    }

    const setDefault = ()=>{
        if(customerAddress.value.customer_address_id){
            setAddressToDefault(customerAddress.value, {
                afterFetch: (ctx)=>{
                    emit("setNewDefault", customerAddress.value.customer_address_id);
                }
            })
        }
    }

</script>